import { AgGridModule } from 'ag-grid-angular';
import {
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  MasterDetailModule,
  ModuleRegistry,
  ServerSideRowModelModule,
  SideBarModule,
} from 'ag-grid-enterprise';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';

import {
  ApiModule,
  Configuration,
} from '@compumark/analyst-selection-tool-client';
import {
  BrandContextComponentsModule,
  ClassesFormatPipe,
  FilterChipsModule,
  GlasspaneModule,
  GlasspaneService,
} from '@compumark/brand-context-components';
import {
  BrandContextApiModule,
  BrandContextConfiguration,
} from '@compumark/brand-context-components-backend-client';
import {
  UserPreferencesApiModule,
  UserPreferencesConfiguration,
} from '@compumark/user-preferences-backend-specifications';

import { environment } from './../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { NotifyOnErrorInterceptor } from './notification/interceptors/notify-on-error.interceptor';
import { NotificationModule } from './notification/notification.module';
import { AuthenticationInterceptor } from './security/interceptors/authentication.interceptor';
import { SecurityModule } from './security/security.module';
import { UserService } from './security/services/user.service';
import { DeliveryDateRendererComponent } from './tasks-overview/components/delivery-date-renderer/delivery-date-renderer.component';
import { DetailsComponent } from './tasks-overview/components/details/details.component';
import { ResultsLinkRendererComponent } from './tasks-overview/components/results-link-renderer/results-link-renderer.component';
import { TasksOverviewDatasource } from './tasks-overview/components/services/tasks-overview-datasource.service';
import { TimeZonedDatePipe } from './tasks-overview/components/services/time-zoned-date.pipe';
import { TasksOverviewComponent } from './tasks-overview/components/tasks-overview/tasks-overview.component';
import { TaskFilterChangeService } from './tasks-overview/components/services/task-filter-change.service';

import { AdminModule } from './admin/admin.module';

import { TaskFilterModelService } from './tasks-overview/components/services/task-filter-model.service';
import { MatButtonModule } from '@angular/material/button';
import { MatTreeModule } from '@angular/material/tree';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { QuillModule } from 'ngx-quill';
import { AuthorizationInterceptor } from './security/interceptors/authorization.interceptor';
import { TableColumnService } from './results/services/table-column-service';
import { TimeLeftRendererComponent } from './tasks-overview/components/time-left-renderer/time-left-renderer.component';
import { AnalyticsService } from './security/services/analytics.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { GlobalHeaderComponent } from './header/component/global-header/global-header.component';
import { HoldCellRendererComponent } from './tasks-overview/components/hold-cell-renderer/hold-cell-renderer.component';
import { HoldPopupComponent } from './tasks-overview/components/holdpopup/hold-popup.component';
import { QualityIconRendererComponent } from './tasks-overview/components/quality-icon-renderer/quality-icon-renderer.component';
import { ConfirmQualityDialogComponent } from './tasks-overview/components/quality-icon-renderer/confirm-quality-dialog/confirm-quality-dialog/confirm-quality-dialog.component';
import { BrowserDetectionService } from './tasks-overview/components/services/browser-detection.service';
import { TaskDetailsComponent } from './tasks-overview/components/details/task-details-renderer/task-details.component';
import { CopyrightTitleTaskDetailsComponent } from './tasks-overview/components/details/copyright-title-task-details-renderer/copyright-title-task-details.component';
import { PreferenceStorageService } from './results/services/preference-storage.service';
import { TooManyResultsDialogComponent } from './tasks-overview/components/too-many-results-dialog/too-many-results-dialog.component';
import { LoadingComponent } from './loading/loading.component';
import { FilterLockService } from './results/services/filter-lock.service';
import { FilterOperationService } from './results/services/filter-operation.service';
import { TrademarkFilterComponent } from './tasks-overview/components/filter/trademark-filter/trademark-filter.component';

import { PreviewLogoRendererComponent } from './tasks-overview/components/preview-logo-renderer/preview-logo-renderer.component';
import { PreviewSearchComponent } from './tasks-overview/components/preview-search/preview-search.component';

import { PreviewValidationComponent } from './tasks-overview/components/preview-validation/preview-validation.component';

import { ConfirmFrequencyMatrixDialogComponent } from './confirm-frequency-matrix-dialog/confirm-frequency-matrix-dialog.component';
import { DomainNamesScreenshotMessageDialogComponent } from './results/domain-names-screenshot-message-dialog/domain-names-screenshot-message-dialog.component';
import { MatRadioModule } from '@angular/material/radio';
import { MatTableModule } from '@angular/material/table';
import {
  HeaderComponent,
  HeaderGlobalComponent,
  FooterModule,
} from '@cdx/ngx-branding';
import { TranslateModule } from '@ngx-translate/core';

ModuleRegistry.registerModules([
  MasterDetailModule,
  ServerSideRowModelModule,
  SideBarModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
]);

export function apiModuleConfigurationFactory(): Configuration {
  return new Configuration({
    basePath: environment.backendBaseUrl,
    withCredentials: true,
  });
}

export function windowFactory(): Window {
  return window;
}

export function brandContextModuleConfigurationFactory(): BrandContextConfiguration {
  return new BrandContextConfiguration({
    basePath: environment.backendBaseUrl + '/brand-context',
    withCredentials: true,
  });
}

export function userPreferenceModuleConfigurationFactory(): UserPreferencesConfiguration {
  return new UserPreferencesConfiguration({
    basePath: environment.backendBaseUrl,
    withCredentials: true,
  });
}

@NgModule({
  declarations: [
    AppComponent,
    TasksOverviewComponent,
    DetailsComponent,
    DeliveryDateRendererComponent,
    ResultsLinkRendererComponent,
    TimeZonedDatePipe,
    TimeLeftRendererComponent,
    GlobalHeaderComponent,
    HoldCellRendererComponent,
    HoldPopupComponent,
    QualityIconRendererComponent,
    ConfirmQualityDialogComponent,
    TaskDetailsComponent,
    CopyrightTitleTaskDetailsComponent,
    TooManyResultsDialogComponent,
    LoadingComponent,
    TrademarkFilterComponent,
    PreviewLogoRendererComponent,
    PreviewSearchComponent,
    PreviewValidationComponent,
    ConfirmFrequencyMatrixDialogComponent,
    DomainNamesScreenshotMessageDialogComponent,
  ],
  imports: [
    ApiModule.forRoot(apiModuleConfigurationFactory),
    BrandContextApiModule.forRoot(brandContextModuleConfigurationFactory),
    UserPreferencesApiModule.forRoot(userPreferenceModuleConfigurationFactory),
    BrandContextComponentsModule,
    BrowserModule,
    BrowserAnimationsModule,
    FilterChipsModule,
    HttpClientModule,
    AppRoutingModule,
    MatIconModule,
    RouterModule,
    MatTabsModule,
    AgGridModule,
    SecurityModule,
    NotificationModule,
    AdminModule,
    FormsModule,
    MatButtonModule,
    MatTreeModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    QuillModule.forRoot(),
    MatDialogModule,
    MatProgressSpinnerModule,
    GlasspaneModule,
    MatTooltipModule,
    MatRadioModule,
    MatTableModule,
    HeaderComponent,
    HeaderGlobalComponent,
    FooterModule,
    TranslateModule.forRoot(),
  ],
  providers: [
    AnalyticsService,
    BrowserDetectionService,
    ClassesFormatPipe,
    GlasspaneService,
    TasksOverviewDatasource,
    TaskFilterChangeService,
    TaskFilterModelService,
    UserService,
    TableColumnService,
    PreferenceStorageService,
    FilterLockService,
    FilterOperationService,

    { provide: 'nativeWindow', useFactory: windowFactory },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthenticationInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthorizationInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NotifyOnErrorInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
